import React, { useState } from 'react';
import { Loader2, Save, ShieldCheck, AlertCircle } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';

/**
 * PayoutBankDetailsForm — shared form for the author's self-edit page
 * and the admin's edit-on-behalf modal.
 *
 * Pass a unique `key` from the parent (e.g. `initial?.updated_at || 'new'`)
 * whenever the initial doc changes — that forces a clean remount and saves
 * us from a useEffect sync-from-props pattern.
 */
const PayoutBankDetailsForm = ({
  initial = null,
  onSubmit,
  submitting = false,
  submitLabel = 'Save bank details',
  helperNote = null,
}) => {
  const [form, setForm] = useState(() => ({
    holder_name: initial?.holder_name || '',
    bank_name: initial?.bank_name || '',
    account_number: initial?.account_number || '',
    iban: initial?.iban || '',
    routing_number: initial?.routing_number || '',
    swift_bic: initial?.swift_bic || '',
    account_type: initial?.account_type || '',
    bank_address: initial?.bank_address || '',
    country: initial?.country || '',
    currency: initial?.currency || '',
    tax_id: initial?.tax_id || '',
    notes: initial?.notes || '',
  }));

  // Live country-aware payable check — mirrors the backend's _is_payable
  // so the indicator below the form switches red→green BEFORE the user
  // hits Save. Same rules as the server: US needs routing+account; intl
  // needs SWIFT + (IBAN or account); blank country falls back to lenient.
  const _country = (form.country || '').trim().toLowerCase();
  const _isUS =
    !!_country &&
    (['us', 'usa'].includes(_country) ||
      ['united states', 'usa', 'u.s.a', 'u.s.'].some((h) => _country.includes(h)));

  const trimmed = (k) => (form[k] || '').trim();
  const haveHolder = !!trimmed('holder_name');
  const haveBank = !!trimmed('bank_name');
  const haveAcct = !!trimmed('account_number');
  const haveIban = !!trimmed('iban');
  const haveRouting = !!trimmed('routing_number');
  const haveSwift = !!trimmed('swift_bic');

  let payableMessage = '';
  let isPayable = false;
  if (!haveHolder || !haveBank) {
    payableMessage = 'Add the account holder and bank name to start.';
  } else if (!_country) {
    isPayable = haveAcct || haveIban;
    if (!isPayable) payableMessage = 'Add an account number or IBAN.';
  } else if (_isUS) {
    isPayable = haveAcct && haveRouting;
    if (!haveAcct) payableMessage = 'US accounts need an Account Number.';
    else if (!haveRouting) payableMessage = 'US accounts need a 9-digit Routing Number.';
  } else {
    const haveDest = haveAcct || haveIban;
    isPayable = haveDest && haveSwift;
    if (!haveDest) payableMessage = 'International accounts need an IBAN or Account Number.';
    else if (!haveSwift) payableMessage = 'International accounts need a SWIFT / BIC code.';
  }

  // Pattern-level checks — purely visual; the backend will reject malformed
  // inputs with 422 too.
  const routingDigits = trimmed('routing_number').replace(/[\s-]+/g, '');
  const routingInvalid = !!routingDigits && !/^\d{9}$/.test(routingDigits);
  const currencyTrim = trimmed('currency').toUpperCase();
  const currencyInvalid = !!currencyTrim && !/^[A-Z]{3}$/.test(currencyTrim);
  const swiftClean = trimmed('swift_bic').toUpperCase().replace(/\s+/g, '');
  const swiftInvalid =
    !!swiftClean && !/^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(swiftClean);
  const hasPatternErrors = routingInvalid || currencyInvalid || swiftInvalid;

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || hasPatternErrors) return;
    await onSubmit(form);
  };

  const fieldErrorClass = (bad) =>
    bad
      ? 'bg-white/5 border-red-500/60 focus-visible:ring-red-500/40 text-white mt-1'
      : 'bg-white/5 border-white/10 text-white mt-1';

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="payout-bank-form">
      {/* Required core */}
      <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/[0.04] p-4 space-y-4">
        <div className="flex items-center gap-2 text-emerald-300 text-xs font-semibold uppercase tracking-wider">
          <ShieldCheck className="h-3.5 w-3.5" /> Core payout fields
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-white text-sm">Account Holder Name *</Label>
            <Input
              value={form.holder_name}
              onChange={set('holder_name')}
              placeholder="As shown on the bank account"
              className="bg-white/5 border-white/10 text-white mt-1"
              data-testid="pbd-holder_name"
            />
          </div>
          <div>
            <Label className="text-white text-sm">Bank Name *</Label>
            <Input
              value={form.bank_name}
              onChange={set('bank_name')}
              placeholder="e.g. Chase, Wells Fargo, HDFC Bank"
              className="bg-white/5 border-white/10 text-white mt-1"
              data-testid="pbd-bank_name"
            />
          </div>
          <div>
            <Label className="text-white text-sm">Account Number</Label>
            <Input
              value={form.account_number}
              onChange={set('account_number')}
              placeholder="US/Domestic account number"
              className="bg-white/5 border-white/10 text-white mt-1 font-mono"
              data-testid="pbd-account_number"
            />
          </div>
          <div>
            <Label className="text-white text-sm">IBAN</Label>
            <Input
              value={form.iban}
              onChange={set('iban')}
              placeholder="For international transfers"
              className="bg-white/5 border-white/10 text-white mt-1 font-mono"
              data-testid="pbd-iban"
            />
          </div>
        </div>
        <p className="text-[11px] text-emerald-200/70">
          You need either an Account Number or an IBAN — whichever your bank uses.
        </p>
      </div>

      {/* Routing / SWIFT */}
      <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 space-y-4">
        <div className="flex items-center gap-2 text-gray-300 text-xs font-semibold uppercase tracking-wider">
          Routing &amp; bank network
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-white text-sm">Routing Number (US ACH)</Label>
            <Input
              value={form.routing_number}
              onChange={set('routing_number')}
              placeholder="9-digit ABA routing"
              className={`${fieldErrorClass(routingInvalid)} font-mono`}
              data-testid="pbd-routing_number"
            />
            {routingInvalid && (
              <p className="text-[11px] text-red-400 mt-1" data-testid="pbd-routing_number-error">
                Must be exactly 9 digits.
              </p>
            )}
          </div>
          <div>
            <Label className="text-white text-sm">SWIFT / BIC</Label>
            <Input
              value={form.swift_bic}
              onChange={set('swift_bic')}
              placeholder="e.g. CHASUS33"
              className={`${fieldErrorClass(swiftInvalid)} font-mono uppercase`}
              data-testid="pbd-swift_bic"
            />
            {swiftInvalid && (
              <p className="text-[11px] text-red-400 mt-1" data-testid="pbd-swift_bic-error">
                Must be 8 or 11 chars (4 bank + 2 country + 2 location [+ 3 branch]).
              </p>
            )}
          </div>
          <div>
            <Label className="text-white text-sm">Account Type</Label>
            <select
              value={form.account_type}
              onChange={set('account_type')}
              className="w-full mt-1 h-10 rounded-md bg-white/5 border border-white/10 text-white px-3 text-sm"
              data-testid="pbd-account_type"
            >
              <option value="">—</option>
              <option value="checking">Checking</option>
              <option value="savings">Savings</option>
            </select>
          </div>
        </div>
      </div>

      {/* Country / currency / tax */}
      <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 space-y-4">
        <div className="flex items-center gap-2 text-gray-300 text-xs font-semibold uppercase tracking-wider">
          Geography &amp; tax
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-white text-sm">Country</Label>
            <Input
              value={form.country}
              onChange={set('country')}
              placeholder="e.g. United States"
              className="bg-white/5 border-white/10 text-white mt-1"
              data-testid="pbd-country"
            />
          </div>
          <div>
            <Label className="text-white text-sm">Currency</Label>
            <Input
              value={form.currency}
              onChange={set('currency')}
              placeholder="USD, EUR, INR, …"
              className={`${fieldErrorClass(currencyInvalid)} uppercase`}
              maxLength={8}
              data-testid="pbd-currency"
            />
            {currencyInvalid && (
              <p className="text-[11px] text-red-400 mt-1" data-testid="pbd-currency-error">
                Must be a 3-letter ISO code (USD, EUR, INR, …).
              </p>
            )}
          </div>
          <div>
            <Label className="text-white text-sm">Tax ID (optional)</Label>
            <Input
              value={form.tax_id}
              onChange={set('tax_id')}
              placeholder="W-9 / PAN / GSTIN / etc."
              className="bg-white/5 border-white/10 text-white mt-1 font-mono"
              data-testid="pbd-tax_id"
            />
          </div>
        </div>
        <div>
          <Label className="text-white text-sm">Bank Address</Label>
          <Input
            value={form.bank_address}
            onChange={set('bank_address')}
            placeholder="Branch address — helps for international wires"
            className="bg-white/5 border-white/10 text-white mt-1"
            data-testid="pbd-bank_address"
          />
        </div>
      </div>

      {/* Notes */}
      <div>
        <Label className="text-white text-sm">Notes</Label>
        <textarea
          value={form.notes}
          onChange={set('notes')}
          rows={3}
          placeholder="Alt payment methods (PayPal email, Wise, etc.) or anything the admin should know"
          className="w-full mt-1 rounded-md bg-white/5 border border-white/10 text-white px-3 py-2 text-sm"
          data-testid="pbd-notes"
        />
      </div>

      <div className="flex items-center justify-between pt-2">
        <div className="flex items-center gap-2 text-xs" data-testid="pbd-payable-status">
          {hasPatternErrors ? (
            <span className="inline-flex items-center gap-1 text-red-400">
              <AlertCircle className="h-3.5 w-3.5" /> Fix the errors above before saving.
            </span>
          ) : isPayable ? (
            <span className="inline-flex items-center gap-1 text-emerald-300">
              <ShieldCheck className="h-3.5 w-3.5" /> Ready for payout
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-amber-300">
              <AlertCircle className="h-3.5 w-3.5" /> {payableMessage || 'Fill in more fields to be payable.'}
            </span>
          )}
        </div>
        <Button
          type="submit"
          disabled={submitting || hasPatternErrors}
          className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="pbd-submit"
        >
          {submitting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          {submitLabel}
        </Button>
      </div>
      {helperNote && <p className="text-[11px] text-gray-500">{helperNote}</p>}
    </form>
  );
};

export default PayoutBankDetailsForm;
